import React from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";

import { Sentry } from "src/sentry";

import { AuthProvider, useAuth } from "src/contexts/AuthContext";
import { useWebPush } from "src/features/notifications/hooks/useWebPush";
import { isDedicatedAppHost, onMarketingHost, goToApp, appUrl, shouldRenderMarketing } from "src/config/hosts";
import MarketingSite from "src/features/marketing/MarketingSite";
import { SearchProvider } from "src/contexts/SearchContext";
import { NetworkStatusProvider } from "src/contexts/NetworkStatusContext";
import ImpersonationBanner from "src/components/ImpersonationBanner";
import OfflineBanner from "src/components/OfflineBanner";
import { LoginPage, SignupPage } from "src/components/auth/auth-pages";
import Dashboard from "src/components/Dashboard";
import { AppShell } from "src/components/layout/AppShell";
// import { OtpVerifyPage } from "src/components/auth/OtpVerifyPage";
// import { OtpSuccessPage } from "src/components/auth/OtpSuccessPage";
// import { OtpFailurePage } from "src/components/auth/OtpFailurePage";
// import { VerificationPage } from "src/pages/VerificationPage";
import LeadsPage from "src/pages/LeadsPage";
import LeadDetailPage from "src/pages/LeadDetailPage";
import QualificationPage from "src/pages/QualificationPage";
import DealsPage from "src/pages/DealsPage";
import DealDetailPage from "src/pages/DealDetailPage";
import BookingsPage from "src/pages/BookingsPage";
import BookingDetailPage from "src/pages/BookingDetailPage";
import ContactsPage from "src/pages/ContactsPage";
import SiteVisitsPage from "src/pages/SiteVisitsPage";
import DocumentsPage from "src/pages/DocumentsPage";
import PortalHomePage from "src/pages/portal/PortalHomePage";
import { ToastProvider, Toaster } from "src/components/ui/toast";
import ConfigurationsLayout from "src/pages/configurations/ConfigurationsLayout";
import OrganizationGeneralPage from "src/pages/configurations/OrganizationGeneralPage";
import DummyConfigPage from "src/pages/configurations/DummyConfigPage";
import IntegrationsPage from "src/pages/configurations/IntegrationsPage";
import PaymentPlansPage from "src/pages/configurations/PaymentPlansPage";
import MembersPage from "src/pages/configurations/MembersPage";
import InvitationsPage from "src/pages/configurations/InvitationsPage";
import RolesPage from "src/pages/configurations/RolesPage";
import AuditLogsPage from "src/pages/configurations/AuditLogsPage";
import ResetPasswordPage from "src/pages/configurations/ResetPasswordPage";
import MyInformationPage from "src/pages/configurations/MyInformationPage";
import NotificationSettingsPage from "src/pages/configurations/NotificationSettingsPage";
import TemplatesPage from "src/pages/configurations/TemplatesPage";
import TagsPage from "src/pages/configurations/TagsPage";
import TeamsPage from "src/pages/configurations/TeamsPage";
import QrCodesPage from "src/pages/configurations/QrCodesPage";
import AcceptInvitationPage from "src/pages/AcceptInvitationPage";
import VerifyCustomerEmailPage from "src/pages/VerifyCustomerEmailPage";
import PropertiesLayout from "src/pages/properties/PropertiesLayout";
import SellerPropertiesPage from "src/pages/properties/SellerPropertiesPage";
import BuyerRequirementsPage from "src/pages/properties/BuyerRequirementsPage";
import RentalPropertiesPage from "src/pages/properties/RentalPropertiesPage";
import ProjectsPage from "src/pages/projects/ProjectsPage";
import ProjectDashboardPage from "src/pages/projects/ProjectDashboardPage";
import AttendancePage from "src/pages/attendance/AttendancePage";
import AttendanceReportsPage from "src/pages/attendance/AttendanceReportsPage";
import GeofencingPage from "src/pages/attendance/GeofencingPage";
import ReportsLayout from "src/pages/reports/ReportsLayout";
import OverviewReportPage from "src/pages/reports/OverviewReportPage";
import FunnelReportPage from "src/pages/reports/FunnelReportPage";
import VelocityReportPage from "src/pages/reports/VelocityReportPage";
import WinLossReportPage from "src/pages/reports/WinLossReportPage";
import CollectionsReportPage from "src/pages/reports/CollectionsReportPage";
import InventoryReportPage from "src/pages/reports/InventoryReportPage";
import WebsiteEnquiriesReportPage from "src/pages/reports/WebsiteEnquiriesReportPage";
import HomepageEditorPage from "src/pages/website/HomepageEditorPage";
import MyDayPage from "src/pages/MyDayPage";
import DistributionRulesPage from "src/pages/configurations/DistributionRulesPage";
import PipelinesConfigPage from "src/pages/configurations/PipelinesConfigPage";
import ChannelPartnersPage from "src/pages/configurations/ChannelPartnersPage";
import DocumentTemplatesPage from "src/pages/configurations/DocumentTemplatesPage";
import ChannelPartnerReportPage from "src/pages/reports/ChannelPartnerReportPage";
import CollectionsLayout from "src/pages/collections/CollectionsLayout";
import CollectionsDashboardPage from "src/pages/collections/CollectionsDashboardPage";
import AgingReportPage from "src/pages/collections/AgingReportPage";
import CollectionSettingsPage from "src/pages/collections/CollectionSettingsPage";
import NotFoundPage from "src/pages/NotFoundPage";
import OrganizationDeactivatedPage from "src/pages/OrganizationDeactivatedPage";
import "src/i18n";

function ProtectedRoute({ children }) {
  const { isAuthenticated, isCustomer, isOrgDeactivated } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  // The org's subscription lapsed (or a superadmin suspended it): the whole
  // CRM is replaced by the blocked screen. The backend 403s the APIs anyway.
  if (isOrgDeactivated) return <OrganizationDeactivatedPage />;

  // Customers (buyers) never see the staff CRM — bounce them to their portal.
  if (isCustomer) return <Navigate to="/portal" replace />;

  // RERA verification gate removed — users are onboarded by us, so no /verify redirect.
  // if (currentUser && ["broker", "developer"].includes(currentUser.account_type) && currentUser.rera_verification_status !== "verified") {
  //   return <Navigate to="/verify" replace />;
  // }

  return children;
}

// Inventory (projects/towers/units) is developer-only; brokers are redirected.
function DeveloperRoute({ children }) {
  const { currentUser } = useAuth();
  if (currentUser && currentUser.account_type !== "developer") {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

// Persistent layout for the authenticated CRM. ProtectedRoute runs first and
// short-circuits (to /login, /portal, or the deactivated screen) before AppShell
// mounts, so the sidebar only ever renders for a permitted user. Because this
// element stays mounted across every child route, navigation swaps the <Outlet>
// content without tearing down and rebuilding the shell.
function ProtectedShell() {
  return (
    <ProtectedRoute>
      <AppShell>
        <Outlet />
      </AppShell>
    </ProtectedRoute>
  );
}

// Developer-only layout, nested inside ProtectedShell so the shell persists even
// when the guard redirects a non-developer away.
function DeveloperOutlet() {
  return (
    <DeveloperRoute>
      <Outlet />
    </DeveloperRoute>
  );
}

// Customers (portal users) don't go through the staff RERA onboarding, so
// the portal only needs a signed-in check. Staff who land here are sent back
// to the CRM so the two worlds stay separate.
function CustomerPortalRoute({ children }) {
  const { isAuthenticated, isCustomer, isOrgDeactivated } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (isOrgDeactivated) return <OrganizationDeactivatedPage />;
  if (!isCustomer) return <Navigate to="/dashboard" replace />;
  return children;
}

function GuestRoute({ children }) {
  const { isAuthenticated, isCustomer } = useAuth();
  if (isAuthenticated) {
    const target = isCustomer ? "/portal" : "/dashboard";
    // If we're on the marketing host, cross over to the app host (full
    // navigation). On the app host / in dev, this is a same-origin SPA nav.
    if (onMarketingHost()) {
      goToApp(target);
      return null;
    }
    return <Navigate to={target} replace />;
  }
  return children;
}

// The root path "/" only reaches here when NOT rendering the marketing SPA
// (which App intercepts via shouldRenderMarketing()) — i.e. on the dedicated app
// host. Send to the dashboard/portal (authed) or login.
function RootRoute() {
  const { isAuthenticated, isCustomer } = useAuth();
  if (isAuthenticated) {
    return <Navigate to={isCustomer ? "/portal" : "/dashboard"} replace />;
  }
  return <Navigate to="/login" replace />;
}

// Callers only get account-level settings, so their configuration landing page
// is My Information rather than the org-wide General page.
const CALLER_CONFIG_HOME = "/configurations/my-information";

// Org-wide configuration pages are hidden from callers; block direct URL
// access and bounce them to their account settings.
function OrgConfigRoute({ children }) {
  const { isCaller } = useAuth();
  if (isCaller) return <Navigate to={CALLER_CONFIG_HOME} replace />;
  return children;
}

function ConfigurationsRedirect() {
  const { isCaller } = useAuth();
  return <Navigate to={isCaller ? CALLER_CONFIG_HOME : "/configurations/general"} replace />;
}

function AppRoutes() {
  const { isAuthenticated } = useAuth();
  useWebPush(isAuthenticated);

  return (
    <Routes>
      <Route path="/login" element={<GuestRoute><LoginPage /></GuestRoute>} />
      <Route path="/signup" element={<Navigate to="/login" replace />} />
      <Route path="/accept-invitation/:uuid" element={<AcceptInvitationPage />} />
      <Route path="/verify-customer-email/:token" element={<VerifyCustomerEmailPage />} />
      <Route path="/" element={<RootRoute />} />

      {/* Customer portal (read-only buyer view) — its own shell, outside the CRM. */}
      <Route path="/portal" element={<CustomerPortalRoute><PortalHomePage /></CustomerPortalRoute>} />
      {/* <Route path="/otp_verify" element={<OtpVerifyPage />} />
      <Route path="/otp_success" element={<OtpSuccessPage />} />
      <Route path="/otp_failure" element={<OtpFailurePage />} /> */}
      {/* <Route path="/verify" element={<VerificationPage />} /> */}

      {/* Authenticated CRM. The AppShell (sidebar + providers) is mounted ONCE
          here as a persistent layout; every child renders through its <Outlet>,
          so navigating between pages only swaps the inner content instead of
          tearing down and rebuilding the whole shell. Guards (auth, customer,
          org-deactivated) live in ProtectedShell and short-circuit before the
          shell renders. */}
      <Route element={<ProtectedShell />}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/my-day" element={<MyDayPage />} />
        <Route path="/leads" element={<LeadsPage />} />
        <Route path="/leads/:id" element={<LeadDetailPage />} />
        <Route path="/qualification" element={<QualificationPage />} />
        <Route path="/deals" element={<DealsPage />} />
        <Route path="/deals/:id" element={<DealDetailPage />} />
        <Route path="/bookings" element={<BookingsPage />} />
        <Route path="/bookings/:id" element={<BookingDetailPage />} />
        <Route path="/contacts" element={<ContactsPage />} />
        <Route path="/site-visits" element={<SiteVisitsPage />} />
        <Route path="/documents" element={<DocumentsPage />} />

        {/* Attendance — marking is also available as a dashboard widget; this
            full-page version (with selfie capture) stays deep-linkable. */}
        <Route path="/attendance" element={<AttendancePage />} />

        {/* Collections (developer-only) */}
        <Route element={<DeveloperOutlet />}>
          <Route path="/collections" element={<Navigate to="/collections/dashboard" replace />} />
          <Route element={<CollectionsLayout />}>
            <Route path="/collections/dashboard" element={<CollectionsDashboardPage />} />
            <Route path="/collections/aging" element={<AgingReportPage />} />
            <Route path="/collections/settings" element={<CollectionSettingsPage />} />
          </Route>

          {/* Projects / Inventory Routes (developer-only) */}
          <Route path="/projects" element={<ProjectsPage />} />
          <Route path="/projects/:id" element={<ProjectDashboardPage />} />
        </Route>

        {/* Properties Routes */}
        <Route path="/properties" element={<Navigate to="/properties/seller" replace />} />
        <Route element={<PropertiesLayout />}>
          <Route path="/properties/seller" element={<SellerPropertiesPage />} />
          <Route path="/properties/buyer" element={<BuyerRequirementsPage />} />
          <Route path="/properties/rental" element={<RentalPropertiesPage />} />
        </Route>

        {/* Reports — tabbed layout */}
        <Route path="/reports" element={<Navigate to="/reports/overview" replace />} />
        <Route element={<ReportsLayout />}>
          <Route path="/reports/overview" element={<OverviewReportPage />} />
          <Route path="/reports/funnel" element={<FunnelReportPage />} />
          <Route path="/reports/velocity" element={<VelocityReportPage />} />
          <Route path="/reports/win-loss" element={<WinLossReportPage />} />
          <Route path="/reports/collections" element={<CollectionsReportPage />} />
          <Route path="/reports/inventory" element={<InventoryReportPage />} />
          <Route path="/reports/website-enquiries" element={<WebsiteEnquiriesReportPage />} />
          <Route path="/reports/channel-partners" element={<ChannelPartnerReportPage />} />
          <Route path="/reports/attendance" element={<AttendanceReportsPage />} />
        </Route>

        {/* Marketing website editor */}
        <Route path="/website" element={<HomepageEditorPage />} />

        {/* Configurations Routes */}
        <Route path="/configurations" element={<ConfigurationsRedirect />} />
        <Route element={<ConfigurationsLayout />}>
          <Route path="/configurations/general" element={<OrgConfigRoute><OrganizationGeneralPage /></OrgConfigRoute>} />
          <Route path="/organizations/:slug/edit" element={<OrgConfigRoute><OrganizationGeneralPage /></OrgConfigRoute>} />
          <Route path="/configurations/integrations" element={<OrgConfigRoute><IntegrationsPage /></OrgConfigRoute>} />
          <Route path="/configurations/payment-plans" element={<PaymentPlansPage />} />
          <Route path="/configurations/members" element={<MembersPage />} />
          <Route path="/configurations/invitations" element={<InvitationsPage />} />
          <Route path="/configurations/roles" element={<RolesPage />} />
          <Route path="/configurations/audit-log" element={<AuditLogsPage />} />
          <Route path="/configurations/geofencing" element={<GeofencingPage />} />
          <Route path="/configurations/reset-password" element={<ResetPasswordPage />} />
          <Route path="/configurations/my-information" element={<MyInformationPage />} />
          <Route path="/configurations/notifications" element={<NotificationSettingsPage />} />
          <Route path="/configurations/templates" element={<TemplatesPage />} />
          <Route path="/configurations/tags" element={<TagsPage />} />
          <Route path="/configurations/teams" element={<TeamsPage />} />
          <Route path="/configurations/qr-codes" element={<QrCodesPage />} />
          <Route path="/configurations/pipelines" element={<PipelinesConfigPage />} />
          <Route path="/configurations/distribution" element={<DistributionRulesPage />} />
          <Route path="/configurations/channel-partners" element={<ChannelPartnersPage />} />
          <Route path="/configurations/document-templates" element={<DocumentTemplatesPage />} />
          <Route path="/configurations/:tab" element={<DummyConfigPage />} />
        </Route>
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function AppFallback() {
  return (
    <div style={{ padding: "2rem", textAlign: "center" }}>
      <h1>Something went wrong</h1>
      <p>The error has been reported. Please refresh the page to try again.</p>
    </div>
  );
}

export default function App({ bootstrapData = {} }) {
  // The public marketing SPA renders outside the CRM's auth/search providers —
  // it's fully public and shares nothing with the signed-in app. This keeps the
  // marketing bundle from pulling the app's session machinery at runtime.
  if (shouldRenderMarketing()) {
    return (
      <Sentry.ErrorBoundary fallback={<AppFallback />}>
        <BrowserRouter>
          <MarketingSite />
        </BrowserRouter>
      </Sentry.ErrorBoundary>
    );
  }

  return (
    <Sentry.ErrorBoundary fallback={<AppFallback />}>
      <BrowserRouter>
        <NetworkStatusProvider>
          <ToastProvider>
            <AuthProvider initialData={bootstrapData}>
              <SearchProvider>
                <OfflineBanner />
                <ImpersonationBanner />
                <AppRoutes />
                <Toaster />
              </SearchProvider>
            </AuthProvider>
          </ToastProvider>
        </NetworkStatusProvider>
      </BrowserRouter>
    </Sentry.ErrorBoundary>
  );
}
